import React, { useMemo, useState } from 'react';
import Icon from '@minecraft.icon';
import { BasicModal } from '@minecraft.modal';
import PreventDownloadContainer from '@minecraft.prevent-download-container';
import { Carousel } from '@minecraft.molecules';
import { WindowWidthContext } from '@minecraft.utils';
import {
  FlexRow,
  BodyTextLarge,
  Link,
  FlexCol,
  TextDynamic,
  ButtonAsWrapper,
  BodyTextSmall,
  H1Text,
} from '@minecraft.atoms';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import { MediaAudio } from './MediaAudio';
import { MediaVideo } from './MediaVideo';
import { MediaViewerMedia, MediaViewerProps, MediaAudioProps, MediaVideoProps } from './types';

type AudioSlideProps = {
  audioProps: MediaAudioProps;
  isVisible: boolean;
  withDownloadLink: boolean;
};

const AudioSlide: React.FC<AudioSlideProps> = ({ audioProps, isVisible, withDownloadLink }) => {
  const { t } = useTranslation();
  const hasDownloadLink = withDownloadLink && Boolean(audioProps.downloadUrl);

  return (
    <FlexCol
      data-testid="media-viewer-audio-slide"
      alignItems="center"
      justifyContent="center"
      gap="2"
      w="4/5"
      className="cn_mx-auto"
    >
      <FlexRow w="4/5" alignItems="center" justifyContent="center" mdProps={{ maxW: '50vw' }}>
        <MediaAudio isVisible={isVisible} src={audioProps.src} preload="metadata" />
      </FlexRow>
      {audioProps?.nameWithExt && (
        <FlexRow w="4/5" alignItems="center" justifyContent="center" mdProps={{ maxW: '50vw' }}>
          <BodyTextLarge color="inverted" truncatableNoWrap align="center" data-testid="media-audio-name">
            {audioProps.nameWithExt}
          </BodyTextLarge>
        </FlexRow>
      )}
      {hasDownloadLink && (
        <FlexRow w="4/5" alignItems="center" justifyContent="center" mdProps={{ maxW: '50vw' }}>
          <Link to={audioProps.downloadUrl} target="_blank">
            {t('common:button.download')}
          </Link>
        </FlexRow>
      )}
    </FlexCol>
  );
};

type VideoSlideProps = {
  videoProps: MediaVideoProps;
  isVisible: boolean;
  withDownloadLink: boolean;
};

const VideoSlide: React.FC<VideoSlideProps> = ({ videoProps, isVisible, withDownloadLink }) => {
  const { t } = useTranslation();
  const hasDownloadLink = withDownloadLink && Boolean(videoProps.downloadUrl);

  return (
    <FlexCol
      data-testid="media-viewer-video-slide"
      alignItems="center"
      justifyContent="center"
      gap="2"
      w="4/5"
      className="cn_mx-auto"
    >
      <FlexRow w="4/5" alignItems="center" justifyContent="center" mdProps={{ maxW: '50vw' }}>
        <MediaVideo isVisible={isVisible} src={videoProps.src} poster={videoProps.poster} preload="metadata" />
      </FlexRow>
      {videoProps?.nameWithExt && (
        <FlexRow w="4/5" alignItems="center" justifyContent="center" mdProps={{ maxW: '50vw' }}>
          <BodyTextLarge color="inverted" truncatableNoWrap align="center" data-testid="media-video-name">
            {videoProps.nameWithExt}
          </BodyTextLarge>
        </FlexRow>
      )}
      {hasDownloadLink && (
        <FlexRow w="4/5" alignItems="center" justifyContent="center" mdProps={{ maxW: '50vw' }}>
          <Link to={videoProps.downloadUrl} target="_blank">
            {t('common:button.download')}
          </Link>
        </FlexRow>
      )}
    </FlexCol>
  );
};

type CarouselNavBtnProps = {
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  currentSlide?: number;
  slideCount?: number;
};

const PrevBtn: React.FC<CarouselNavBtnProps> = (props) => {
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();
  const className = (props.className || '').replace('slick-prev', '').trim();
  return (
    <ButtonAsWrapper
      aria-label={t('common:button.previous')}
      onClick={props.onClick}
      className={`${className} cn_absolute cn_block`}
      style={{
        ...(props.style || {}),
        top: '50%',
        left: '0',
        right: 'auto',
        transform: 'translateY(-50%)',
        zIndex: 10,
      }}
      data-current-slide={props?.currentSlide}
      data-slide-count={props?.slideCount}
    >
      <Icon name="caret-left" themeColor="inverted" size={isMobile ? 'lg' : '2xl'} />
    </ButtonAsWrapper>
  );
};

const NextBtn: React.FC<CarouselNavBtnProps> = (props) => {
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();
  const className = (props.className || '').replace('slick-next', '').trim();
  return (
    <ButtonAsWrapper
      aria-label={t('common:button.next')}
      onClick={props.onClick}
      className={`${className} cn_absolute cn_block`}
      style={{
        ...(props.style || {}),
        top: '50%',
        left: 'auto',
        right: '0',
        transform: 'translateY(-50%)',
        zIndex: 10,
      }}
      data-current-slide={props?.currentSlide}
      data-slide-count={props?.slideCount}
    >
      <Icon name="caret-right" themeColor="inverted" size={isMobile ? 'lg' : '2xl'} />
    </ButtonAsWrapper>
  );
};

const MediaSlide: React.FC<{
  mediaItem: MediaViewerMedia;
  isVisible: boolean;
  withDownloadLink: boolean;
  preventDownload: boolean;
}> = ({ mediaItem, isVisible, withDownloadLink, preventDownload }) => {
  let Component = null;
  if (mediaItem.type === MediaTypeCodeEnum.Audio) {
    Component = (
      <AudioSlide audioProps={mediaItem.audioProps} isVisible={isVisible} withDownloadLink={withDownloadLink} />
    );
  }

  if (mediaItem.type === MediaTypeCodeEnum.Video) {
    Component = (
      <VideoSlide videoProps={mediaItem.videoProps} isVisible={isVisible} withDownloadLink={withDownloadLink} />
    );
  }

  return (
    <>
      {preventDownload ? (
        <PreventDownloadContainer preventContextMenu>{Component}</PreventDownloadContainer>
      ) : (
        Component
      )}
    </>
  );
};

/**
 * Provider-agnostic replacement for `CloudinaryMediaViewer`. Renders a full
 * screen gallery modal, matching `CloudinaryMediaViewer`'s chrome/styling,
 * but never takes gqlMedia/Cloudinary-shaped objects as props - callers
 * build `mediaList` via a formatting function (e.g.
 * `formatAudioMediaToMediaViewerItem`) before passing it in.
 *
 * Only `AUDIO` items are rendered today; other media types will be added as
 * their own provider-agnostic renderers exist.
 */
export const MediaViewer: React.FC<MediaViewerProps> = ({
  handleCloseModal,
  mediaList: nonValidatedMediaList,
  preventDownload,
  viewerTitle,
  startIndex = 0,
  withDownloadLink = true,
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(startIndex || 0);

  const mediaList = useMemo(() => {
    if (!Array.isArray(nonValidatedMediaList)) {
      return [];
    }

    return nonValidatedMediaList?.filter((media) =>
      [MediaTypeCodeEnum.Audio, MediaTypeCodeEnum.Video].includes(media?.type as MediaTypeCodeEnum)
    );
  }, [nonValidatedMediaList]);

  const hasTitle = Boolean(viewerTitle);
  const mediaListLength = mediaList?.length;
  const hasMultipleMedia = Array.isArray(mediaList) && mediaListLength > 1;
  const hasMedia = Array.isArray(mediaList) && mediaListLength > 0;

  return (
    <BasicModal aria-label={viewerTitle || 'Media viewer'} onCancel={handleCloseModal}>
      <FlexCol
        data-testid="media-viewer"
        className="cn_relative cn_fixed cn_overflow-x-hidden cn_overflow-y-auto cn_atom_py-6 cn_atom_px-6"
        mdProps={{
          className: 'cn_relative cn_fixed cn_overflow-x-hidden cn_overflow-y-auto cn_atom_py-6 cn_atom_px-12',
        }}
        zIndex={1050}
        top={0}
        left={0}
        w="full"
        h="full"
        bgColor="maxContrast"
        bgOpacity={0.85}
        alignItems="flex-start"
      >
        <FlexRow
          w="full"
          wrap="nowrap"
          alignItems="center"
          justifyContent={hasTitle ? 'space-between' : 'flex-end'}
          gap="8"
          grow={0}
        >
          {hasTitle && (
            <TextDynamic
              fontSize="h3"
              fontWeight="semibold"
              data-testid="media-viewer-title"
              data-qa-id="media-viewer-title"
              color="inverted"
            >
              {viewerTitle}
            </TextDynamic>
          )}
          <FlexRow grow={0} shrink={0}>
            <ButtonAsWrapper
              onClick={handleCloseModal}
              data-qa-id="media-viewer-close-button"
              aria-label={t('common:button.close')}
            >
              <Icon themeColor="inverted" name="close" />
            </ButtonAsWrapper>
          </FlexRow>
        </FlexRow>
        {hasMedia ? (
          <Carousel
            data-testid="media-viewer-carousel"
            maxW="full"
            settings={{
              dots: false,
              arrows: hasMultipleMedia,
              infinite: hasMultipleMedia,
              // Without this, a click that arrives while react-slick's internal "animating" flag is
              // still set gets silently dropped - and in some environments (seen in Jest/jsdom, where
              // there's no real CSS transitionend to clear it) that flag never resets, so every click
              // after the first does nothing. false makes a new click cancel the pending transition
              // and take effect immediately instead.
              waitForAnimate: false,
              slidesToShow: 1,
              slidesToScroll: 1,
              speed: 500,
              currentSlide: startIndex,
              updateCurrentSlide: setCurrentIndex,
              prevArrow: <PrevBtn />,
              nextArrow: <NextBtn />,
            }}
          >
            {mediaList.map((mediaItem, index) => (
              <MediaSlide
                withDownloadLink={withDownloadLink}
                key={mediaItem.key}
                mediaItem={mediaItem}
                isVisible={index === currentIndex}
                preventDownload={preventDownload}
              />
            ))}
          </Carousel>
        ) : (
          <FlexRow alignItems="center" mx="auto">
            <H1Text color="inverted">{t('common:media.noMedia')}</H1Text>
          </FlexRow>
        )}
        {hasMedia && (
          <FlexRow
            data-testid="media-viewer-index-count"
            w="full"
            alignItems="center"
            justifyContent="flex-end"
            grow={0}
          >
            <BodyTextSmall color="inverted">
              {currentIndex + 1} of {mediaListLength}
            </BodyTextSmall>
          </FlexRow>
        )}
      </FlexCol>
    </BasicModal>
  );
};
